import { LoaderFunctionArgs } from 'react-router-dom'
import { api, queryClient } from '@/lib/api-client.ts'
import { currStageUsersQuery } from '@/lib/actions/getCurrStageUsers.ts'
import handleApiErr from '@/lib/helpers/handleApiGerError.ts'
import { ReportDetail, ReportIssuesDataT } from '@/routes/projectStages/reports/lib/actions.ts'
import { AxiosResponse } from 'axios'
import { offlineSafeEnsureQueryData } from '@/lib/helpers/offlineSafeQuery.ts'

export const loaderSingleReport = async ({ params }: LoaderFunctionArgs) => {
  if (!params.reportId || !params.projectStageId) {
    throw new Error('No issue ID provided')
  }

  const safe = <T>(q: { queryKey: unknown[]; queryFn: () => Promise<T> }) =>
    offlineSafeEnsureQueryData(queryClient, q.queryKey, q.queryFn).catch(() => undefined)

  try {
    await Promise.all([
      safe(singleReportQuery(params.reportId)),
      safe(currStageUsersQuery(params.projectStageId)),
    ])

    return {
      reportId: params.reportId,
      projectStageId: params.projectStageId,
    }
  } catch (error) {
    // Check if this is a network-related error
    const isNetworkError =
      !navigator.onLine ||
      (error instanceof Error &&
        (error.message.toLowerCase().includes('network') ||
          error.message.toLowerCase().includes('failed to fetch') ||
          (error as { code?: string }).code === 'ERR_NETWORK'))

    if (isNetworkError) {
      return {
        reportId: params.reportId,
        projectStageId: params.projectStageId,
      }
    }
    return handleApiErr(error)
  }
}

export const singleReportQuery = (reportId: string) => ({
  queryKey: ['singleReport', { id: reportId }],
  queryFn: () => getSingleReport(reportId),
  staleTime: 0,
  refetchOnMount: 'always' as const,
})

export const singleReportIssuesQuery = ({
  pageParam,
  reportId,
}: {
  pageParam: number
  reportId: string
}) => ({
  queryKey: ['singleReportIssues', { id: reportId }],
  queryFn: () => getSingleReportIssues({ pageParam, reportId }),
  staleTime: 1000 * 30,
})

export const getSingleReport = async (reportId: string) => {
  if (!reportId) return null
  const response = await api.get(`/reports/${reportId}`)
  return response.data as ReportDetail
}

export const getSingleReportIssues = async ({
  pageParam,
  reportId,
}: {
  reportId: string
  pageParam: number
}) => {
  if (!reportId) return null

  const page = pageParam === 0 ? 1 : pageParam
  const response = await api.get(`/reports/${reportId}/issues?page=${page}`)
  return response.data as ReportIssuesDataT
}

export const editReport = async (report: Partial<ReportDetail>): Promise<AxiosResponse<ReportDetail>> => {
  return api.patch(`/reports/${report.id}`, report)
}
